import { useEffect, useState, useCallback } from 'react';
import './Main.scss';
import './styles.scss';
import { NavBar } from '@/components/organisms/NavBar';
import CryptoJS from 'crypto-js';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	userGetUserRequest,
	userAddEmailToken
} from '@/modules/user/userActions';
import type { RootState } from '@/rootReducer';
import {
	dashboardGetListsRequest,
	dashboardSelectBusiness,
	dashboardSelectList,
	dashboardSetToStale,
	dashboardClickDeleteNote,
	dashboardRemoveAllFromSaveList,
	dashboardSetQuery,
	dashboardResetQuery
} from '@/modules/dashboard/dashboardActions';
import SearchBusinesses from '@/modules/searchBusinesses/SearchBusinesses';
import CommunicationCenterRedesign from '@/modules/communicationCenter/scenes/CommunicationCenterRedesign';
import { ListsManagementPage } from '@/modules/lists/management';
import { ActivityReportsPage } from '@/modules/activity-reports';
import { ReactivationPage } from '@/modules/reactivation';
import ScheduledEmailsRedesign from '@/modules/scheduledEmailsPage/scenes/scheduledEmailsRedesign/ScheduledEmailsRedesign';
import WebFont from 'webfontloader';
import {
	emailGetEmailStatuses,
	emailGetScheduledEmailLimits,
	emailGetEmailTemplatesRequest
} from '@/modules/email/emailActions';
import { ListDetailsPage } from '@/modules/lists/details';
import SequencesRedesign from '@/modules/sequencesRedesign/scenes/SequencesRedesign';
import { Route, Switch, useHistory, Redirect } from 'react-router-dom';
import DemandReports from '@/modules/demandReports/scenes/DemandReports';
import Admin from '@/modules/admin/scenes/Admin';
import { AIPromptPage } from '@/modules/admin/scenes/SandboxPrompts/AIPromptPage';
import EmailOnboarding from '@/modules/emailOnboarding/scenes/EmailOnboarding';
import { FreeTrialModalProvider } from '@/modules/freeTrial/FreeTrialModalContext';
import StripeRedirect from '@/modules/freeTrial/scenes/StripeRedirect';

// Modal Imports
import AddBccModal from '@/modules/user/components/AddBccModal';
import AddUserModal from '@/modules/user/components/AddUserModal';
import OnboardingFlow from '@/modules/onboarding/OnboardingFlow';
import type { BusinessQuery } from '@/modules/dashboard/dashboardTypes';
import HubspotPretrialRedirect from '@/modules/freeTrial/scenes/HubspotPretrialRedirect';
import { PrivateLayout } from '@/layouts/private/layout';
import { useAdminTokenEncryptQuery } from '@/hooks/useAdminTokenEncryptQuery';
import { XIcon } from '@/resources/assets/images';
import { Tooltip } from '@/components/atoms/Tooltip';
import { TOOLTIP_VIEW_LIMIT } from '@/constants';
import { UserRole } from '@/modules/user/userTypes';
import { useUpdateOnboardingStatus } from '@/modules/onboarding/hooks/useUpdateOnboardingStatus';
import { PaywallEmailModal } from '@/components/organisms/PaywallEmailModal';

WebFont.load({
	google: {
		families: [
			'Barlow:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900',
			'Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900'
		]
	}
});

const MainRefactored = () => {
	const [isAuthorized, user, lists, listSelected, query] = useSelector(
		(state: RootState) => [
			state.user.get('isAuthorized'),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.dashboard.getIn(['lists', 'data']).toJS(),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.dashboard.getIn(['query']).toJS()
		],
		shallowEqual
	);

	const history = useHistory();

	const dispatch = useDispatch();

	// Fetch lists when the component mounts
	useEffect(() => {
		dispatch(dashboardGetListsRequest());
	}, [dispatch]);

	const getEmailStatuses = () => dispatch(emailGetEmailStatuses());
	const getScheduledEmailLimits = () =>
		dispatch(emailGetScheduledEmailLimits());
	const getLists = () => dispatch(dashboardGetListsRequest());
	const getUser = () => dispatch(userGetUserRequest());
	const clickDeleteNote = () => dispatch(dashboardClickDeleteNote());
	const selectBusiness = b => dispatch(dashboardSelectBusiness(b));
	const removeAllFromSaveList = () =>
		dispatch(dashboardRemoveAllFromSaveList());
	const selectList = (id: string) => dispatch(dashboardSelectList(id));
	const getEmailTemplates = () => dispatch(emailGetEmailTemplatesRequest());
	const setQuery = (query: BusinessQuery) =>
		dispatch(dashboardSetQuery({ ...query, user }));

	const setStale = () => {
		dispatch(dashboardSetToStale());
	};
	const addUserEmailToken = (email, code, stateToken) =>
		dispatch(userAddEmailToken({ userEmail: email, code, stateToken }));

	const [deleteDataIndex, setDeleteDataIndex] = useState<number | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [showTooltip, setShowTooltip] = useState(false);

	useEffect(() => {
		if (isAuthorized || user?.email) {
			getUser();
		}
	}, [isAuthorized]);

	const { data: adminTokenEncryptData } = useAdminTokenEncryptQuery();
	const adminTokenEncryptKey =
		adminTokenEncryptData?.data?.data?.adminTokenEncryptAPIKey;

	const { mutateAsync: updateOnboardingStatus } = useUpdateOnboardingStatus();

	// Fetch lists again if user email changes
	useEffect(() => {
		if (user?.email) {
			getLists();
			getScheduledEmailLimits();
			getEmailStatuses();
			getEmailTemplates();
		}
	}, [user?.email]);

	// Functions for handling business selection and updates

	// List change handler
	const listChange = useCallback((list: string) => {
		setStale();
		selectBusiness({});
		removeAllFromSaveList();
		selectList(list);
		setQuery({
			count: true,
			skip: 0,
			limit: 50
		});
		const selectedListId = encodeURIComponent(list);
		history.push(`/lists/details/${selectedListId}`);
		const views = parseInt(
			localStorage.getItem('listSavedTooltipViews') || '0',
			10
		);
		if (views < TOOLTIP_VIEW_LIMIT) {
			setShowTooltip(true);
			localStorage.setItem(
				'listSavedTooltipViews',
				(views + 1).toString()
			);
		}
	}, []);

	useEffect(() => {
		if (listSelected?.id) {
			listChange(listSelected?.id);
		}
	}, []);

	const defaultQuery = {
		minDistance: 0,
		maxDistance: 40,
		limit: 50,
		skip: 0,
		sortField: 'score',
		sort: { score: -1 },
		count: true
	};

	useEffect(() => {
		dispatch(dashboardSetQuery({ ...defaultQuery, user }));
		dispatch(dashboardResetQuery());
		selectBusiness(null);
	}, [location.pathname, dispatch]);

	useEffect(() => {
		const e = document.getElementById('delete-note-modal');
		if (isDeleteModalOpen && e) {
			e.classList.remove('delete-modal-closed');
			setTimeout(() => e.classList.remove('delete-modal-fade'), 0);
		}
		if (!isDeleteModalOpen && e) {
			e.classList.add('delete-modal-fade');
			setTimeout(() => e.classList.add('delete-modal-closed'), 500);
		}
	}, [isDeleteModalOpen]);

	const openDeleteNoteModal = useCallback(() => {
		setIsDeleteModalOpen(true);

		const e = document.getElementById('delete-note-modal');

		if (e) {
			e.classList.remove('delete-modal-closed');
			setTimeout(() => e.classList.remove('delete-modal-fade'), 0);
		}
	}, []);

	const closeDeleteNoteModal = () => {
		setIsDeleteModalOpen(false);

		const e = document.getElementById('delete-note-modal');

		if (e) {
			e.classList.add('delete-modal-fade');
			setTimeout(() => e.classList.add('delete-modal-closed'), 500);
		}
	};

	const renderDeleteNoteModal = () => {
		return (
			<div
				className="delete-modal delete-modal-closed delete-modal-fade"
				id="delete-note-modal"
			>
				<div
					className="delete-modal-background"
					onClick={closeDeleteNoteModal}
				/>

				<div className="delete-inner-modal">
					<div className="delete-modal-title">
						Are you sure you want to discard this note? &nbsp;
						<p>This note will be permanently deleted.</p>
					</div>

					<div className="delete-button-holder">
						<button
							type="button"
							className="cancel-delete"
							onClick={closeDeleteNoteModal}
						>
							Cancel
						</button>

						<button
							type="button"
							className="delete-permanently"
							onClick={() => {
								clickDeleteNote();
								closeDeleteNoteModal();
							}}
						>
							<p>Delete permanently</p>
						</button>
					</div>
				</div>
			</div>
		);
	};

	useEffect(() => {
		const adminAccessToken = new URLSearchParams(
			window.location.search
		).get('admin');

		if (adminAccessToken && adminTokenEncryptKey) {
			const adminAccessToken2 = window.location.search.split('admin=')[1];

			const decryptedToken = JSON.parse(
				CryptoJS.AES.decrypt(
					adminAccessToken2,
					adminTokenEncryptKey
				).toString(CryptoJS.enc.Utf8)
			);
			sessionStorage.setItem('admin_token', decryptedToken);

			// Remove the admin token from the URL and reload
			window.location.href = window.location.href.split('?')[0];
		}
	}, [adminTokenEncryptKey]);

	useEffect(() => {
		const { search } = history.location;
		const nylasUrlCode = new URLSearchParams(search).get('code');

		if (nylasUrlCode) {
			addUserEmailToken(user.email, nylasUrlCode, {
				stateToken: undefined
			});
		}
	}, [history]);

	useEffect(() => {
		const unlisten = history.listen(location => {
			if (location.pathname !== '/lists/details/:id') {
				setShowTooltip(false);
			}
		});
		return () => {
			unlisten();
		};
	}, [history]);

	useEffect(() => {
		if (
			history.action === 'POP' &&
			user?.onboarding?.status === 'pending' &&
			history.location.pathname === '/sequences'
		) {
			updateOnboardingStatus({
				status: 'skipped'
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!showTooltip) return;
		const timer = setTimeout(() => {
			setShowTooltip(false);
		}, 3000);
		return () => clearTimeout(timer);
	}, [showTooltip]);

	if (history.location.pathname === '/stripe-redirect') {
		return <StripeRedirect />;
	}

	if (history.location.pathname === '/pretrial/first-login') {
		return <HubspotPretrialRedirect />;
	}

	const isAdmin = user?.role === UserRole.Admin || user?.role === UserRole.VA;

	if (
		user?.onboarding?.status === 'pending' &&
		!history.location.pathname.startsWith('/onboarding') &&
		!sessionStorage.getItem('suppressOnboardingRedirect')
	) {
		history.replace('/onboarding');
		return null; // Stop rendering while redirecting
	}

	return (
		<>
			{isDeleteModalOpen && renderDeleteNoteModal()}

			{showTooltip && (
				<Tooltip.Provider>
					<Tooltip.Root open={showTooltip}>
						<Tooltip.Content
							className="z-[150] overflow-visible bg-transparent"
							style={{ pointerEvents: 'none' }}
						>
							<div className="pointer-events-auto relative top-[23rem]">
								<div
									className="absolute -top-[1.438rem] left-[1rem] z-[140] h-[1.25rem] w-[1.75rem] rounded-md bg-dm-white"
									style={{
										clipPath:
											'polygon(0% 100%, 45% 5%, 50% 5%, 55% 5%, 100% 100%)'
									}}
								/>

								<div
									className="absolute -top-5 left-[1.125rem] z-[141] h-[1rem] w-[1.5rem] rounded-md bg-dm-blue"
									style={{
										clipPath:
											'polygon(0% 100%, 45% 5%, 50% 5%, 55% 5%, 100% 100%)'
									}}
								/>

								<div className="flex h-[3.375rem] w-[20.313rem] items-center justify-between gap-3 rounded-lg border border-dm-white bg-dm-blue p-4 text-dm-white">
									<span className="w-[253px] overflow-hidden font-barlow text-[1rem] leading-[1.188rem]">
										Your list has been saved. You can find
										it in the List management page.
									</span>
									<XIcon
										className="tooltip-close-icon absolute right-2 top-3 cursor-pointer"
										onClick={() => {
											setShowTooltip(false);
										}}
									/>
								</div>
							</div>
						</Tooltip.Content>
					</Tooltip.Root>
				</Tooltip.Provider>
			)}

			<Switch>
				{/* order matters to avoid sidebar layout */}
				<Route path="/onboarding">
					{user?.role !== UserRole.PreTrial ||
					user?.onboarding?.status === 'completed' ? (
						<Redirect to="/search-businesses" />
					) : (
						<OnboardingFlow />
					)}
				</Route>

				{/* Private Layout */}
				<Route>
					<PrivateLayout>
						<Switch>
							<Route path="/communication-center">
								<FreeTrialModalProvider>
									<CommunicationCenterRedesign
										openDeleteNoteModal={
											openDeleteNoteModal
										}
										deleteDataIndex={deleteDataIndex}
										setDeleteDataIndex={setDeleteDataIndex}
										listChange={listChange}
										lists={lists} // Pass lists when they are available
									/>
								</FreeTrialModalProvider>
							</Route>

							<Route path="/search-businesses">
								<FreeTrialModalProvider>
									<SearchBusinesses
										listChange={listChange}
										openDeleteNoteModal={
											openDeleteNoteModal
										}
										deleteDataIndex={deleteDataIndex}
										setDeleteDataIndex={setDeleteDataIndex}
									/>
								</FreeTrialModalProvider>
							</Route>

							<Route path="/activity-reports">
								<ActivityReportsPage />
							</Route>

							<Route path="/scheduled-emails">
								<ScheduledEmailsRedesign />
							</Route>

							<Route path="/lists" exact>
								<ListsManagementPage />
							</Route>

							<Route path="/lists/details/:id">
								<FreeTrialModalProvider>
									<ListDetailsPage />
								</FreeTrialModalProvider>
							</Route>

							<Route path="/sequences">
								<FreeTrialModalProvider>
									<SequencesRedesign />
								</FreeTrialModalProvider>
							</Route>

							<Route path="/demand-reports">
								<DemandReports />
							</Route>

							<Route path="/admin">
								<Admin />
							</Route>

							<Route path="/ai-prompts">
								{isAdmin && user?.features?.aiSandbox ? (
									<AIPromptPage />
								) : (
									<Redirect to="/search-businesses" />
								)}
							</Route>

							<Route path="/verify-email-onboarding">
								<EmailOnboarding />
							</Route>

							<Route path="/reactivation">
								{
									user?.role !== UserRole.PreTrial ? (
										<Redirect to="/search-businesses" />
									) : (
										<ReactivationPage />
								)}
							</Route>

							<Route path="*">
								{history.location.search.includes(
									'admin'
								) ? null : ( // Render the current page without redirecting
									<Redirect to="/search-businesses" />
								)}
							</Route>
						</Switch>
					</PrivateLayout>
				</Route>
			</Switch>

			<AddBccModal />
			<AddUserModal />
		</>
	);
};

export default MainRefactored;
